// src/components/ui/panel.jsx
// The graphite surface (design decision D9): a panel is told apart from the
// page by shade and a 1px top highlight, never by a border; 22px corners;
// sentence-case labels. Used by the chart builder first; other pages move
// from Card to Panel with the light-mode pass.
import * as React from "react"
import { cn } from "@/lib/utils"

const Panel = React.forwardRef(({ className, ...props }, ref) => (
  <section
    ref={ref}
    className={cn(
      "min-w-0 rounded-[22px] bg-[var(--ui-panel)] p-[18px] text-[var(--ui-text)]",
      "shadow-[inset_0_1px_0_var(--ui-panel-highlight)]",
      className
    )}
    {...props}
  />
))
Panel.displayName = "Panel"

const PanelTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn("mb-3 text-sm font-semibold tracking-[-0.01em] text-[var(--ui-text)]", className)} {...props} />
))
PanelTitle.displayName = "PanelTitle"

/** A small sentence-case label above a control or a group. */
const PanelLabel = React.forwardRef(({ className, as: Comp = "p", ...props }, ref) => (
  <Comp ref={ref} className={cn("mb-2 mt-4 block text-xs text-[var(--ui-text-muted)] first:mt-0", className)} {...props} />
))
PanelLabel.displayName = "PanelLabel"

const PanelHint = React.forwardRef(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("mt-1.5 text-[11.5px] leading-snug text-[var(--ui-text-muted)]", className)} {...props} />
))
PanelHint.displayName = "PanelHint"

/** A full-bleed hairline between sections of a panel. */
const PanelDivider = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} role="separator" className={cn("-mx-[18px] my-[18px] h-px bg-[var(--ui-divider)]", className)} {...props} />
))
PanelDivider.displayName = "PanelDivider"

export { Panel, PanelTitle, PanelLabel, PanelHint, PanelDivider }